import React, { useState, useEffect } from 'react';
import api from '../api';
import TelefoneInput from './TelefoneInput';
import PageLayout from './PageLayout';
import { theme, t } from '../theme';
import { norm, formatFatiasKg } from '../utils';
import { FaPlus, FaTimes, FaSearch, FaEdit, FaTrash, FaEye, FaWhatsapp, FaFilePdf, FaPrint, FaCheck, FaClipboardList, FaShareAlt } from 'react-icons/fa';
import { useConfirm } from './ConfirmProvider';
import useIsMounted from '../hooks/useIsMounted';
import { imprimirRecibo } from '../platform/arquivos';
import { isNative } from '../platform/native';

const STATUS_ORC = { rascunho: '#888', enviado: '#3498db', aprovado: '#27ae60', recusado: '#e74c3c' };
const STATUS_ORC_LABEL = { rascunho: 'Rascunho', enviado: 'Enviado', aprovado: 'Aprovado', recusado: 'Recusado' };

function formatarMoedaInput(v) { const n = v.replace(/\D/g, ''); if (!n) return ''; return (parseInt(n, 10) / 100).toFixed(2).replace('.', ','); }
function parseMoeda(v) { if (!v) return 0; return parseFloat(String(v).replace(/\./g, '').replace(',', '.')) || 0; }
const fmt = (v) => `R$ ${(v || 0).toFixed(2).replace('.', ',')}`;
const fmtData = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '';

const Orcamentos = () => {
  const [orcamentos, setOrcamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const confirm = useConfirm();
  const mounted = useIsMounted();

  // Dados auxiliares
  const [clientes, setClientes] = useState([]);
  const [tamanhos, setTamanhos] = useState([]);
  const [recheios, setRecheios] = useState([]);
  const [coberturas, setCoberturas] = useState([]);
  const [fichasMassa, setFichasMassa] = useState([]);

  useEffect(() => { carregarDados(); }, []);
  const queroAbrirNovoRef = React.useRef(
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('novo') === '1'
  );
  useEffect(() => {
    if (loading) return;
    if (!queroAbrirNovoRef.current) return;
    queroAbrirNovoRef.current = false;
    setEditando(null);
    setShowForm(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const [resO, resC, resT, resR, resF] = await Promise.all([
        api.get('/api/orcamentos'),
        api.get('/api/clientes'),
        api.get('/guia/tamanhos'),
        api.get('/guia/recheios'),
        api.get('/fichas/api/fichas-tecnicas'),
      ]);
      if (!mounted.current) return;
      setOrcamentos(resO.data);
      setClientes(resC.data);
      setTamanhos(resT.data);
      setRecheios(resR.data);
      const todasFichas = resF.data?.fichas || [];
      setFichasMassa(todasFichas.filter(f => f.categoria?.toLowerCase() === 'massas de bolo'));
      // Cobertura sai das fichas da categoria "cobertura", igual em Pedidos.
      // Estava saindo da lista de RECHEIOS: dava para escolher "Brigadeiro"
      // como cobertura e o orcamento saia dizendo isso para a cliente.
      setCoberturas(todasFichas.filter(f => (f.categoria || '').toLowerCase().includes('cobertura')));
    } catch {} finally { if (mounted.current) setLoading(false); }
  };

  const deletar = async (id) => {
    if (!await confirm('Remover orçamento?', { danger: true, confirmText: 'Excluir' })) return;
    setOrcamentos(prev => prev.filter(o => o.id !== id));
    try { await api.delete(`/api/orcamentos/${id}`); } catch { carregarDados(); }
  };

  const converterPedido = async (id) => {
    if (!await confirm('Converter este orçamento em pedido?')) return;
    try {
      const res = await api.post(`/api/orcamentos/${id}/converter-pedido`);
      alert(`Pedido #${res.data.pedido_id} criado com sucesso!`);
      carregarDados();
    } catch {}
  };

  const abrirDetalhe = async (id) => {
    try { const res = await api.get(`/api/orcamentos/${id}`); setDetalhe(res.data); } catch {}
  };

  const filtrados = busca
    ? orcamentos.filter(o => norm(o.cliente_nome).includes(norm(busca)))
    : orcamentos;

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>Carregando...</div>;

  return (
    <PageLayout title="Orçamentos" subtitle="Crie e gerencie orçamentos para os seus clientes"
      action={<button onClick={() => { setEditando(null); setShowForm(true); }} style={s.btnPrimary}><FaPlus /> Criar Orçamento</button>}>

      {/* Busca */}
      <div style={{ position: 'relative', marginBottom: 20 }}>
        <FaSearch style={{ position: 'absolute', left: 14, top: 12, color: '#bbb' }} />
        <input placeholder="Buscar por cliente..." value={busca} onChange={e => setBusca(e.target.value)}
          style={{ ...s.input, paddingLeft: 40 }} />
      </div>

      {/* Lista */}
      {filtrados.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#ccc' }}>
          <FaClipboardList size={40} style={{ marginBottom: 12 }} />
          <p>Nenhum orçamento criado</p>
          <button onClick={() => setShowForm(true)} style={{ ...s.btnSmall, background: '#d4807c', color: '#fff' }}>Criar o primeiro</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtrados.map(o => (
            <div key={o.id} style={s.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#2c3e50' }}>{o.cliente_nome || 'Sem cliente'}</div>
                  <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                    {o.itens?.length || 0} {(o.itens?.length || 0) === 1 ? 'item' : 'itens'}
                    {o.data_entrega && ` · Entrega: ${fmtData(o.data_entrega)}`}
                    {o.validade && ` · Válido até: ${fmtData(o.validade)}`}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => abrirDetalhe(o.id)} style={s.iconBtn} title="Ver"><FaEye /></button>
                  <button onClick={() => { setEditando(o); setShowForm(true); }} style={s.iconBtn} title="Editar"><FaEdit /></button>
                  {o.status !== 'aprovado' && <button onClick={() => converterPedido(o.id)} style={{ ...s.iconBtn, color: '#27ae60' }} title="Converter em Pedido"><FaCheck /></button>}
                  <button onClick={() => deletar(o.id)} style={{ ...s.iconBtn, color: '#e74c3c' }} title="Excluir"><FaTrash /></button>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: STATUS_ORC[o.status], background: `${STATUS_ORC[o.status]}15`, padding: '3px 12px', borderRadius: 8 }}>
                  {STATUS_ORC_LABEL[o.status]}
                </span>
                <span style={{ fontSize: 18, fontWeight: 700, color: '#d4807c' }}>{fmt(o.valor_total)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <OrcamentoFormModal
          orcamento={editando}
          clientes={clientes}
          tamanhos={tamanhos}
          recheios={recheios}
          coberturas={coberturas}
          fichasMassa={fichasMassa}
          onClose={() => setShowForm(false)}
          onSave={(data, isEdit) => {
            setShowForm(false);
            if (isEdit) setOrcamentos(prev => prev.map(o => o.id === data.id ? data : o));
            else setOrcamentos(prev => [data, ...prev]);
          }}
        />
      )}

      {detalhe && (
        <OrcamentoDetalheModal orcamento={detalhe} onClose={() => setDetalhe(null)} />
      )}
    </PageLayout>
  );
};


// ===========================================================================
// Form Modal
// ===========================================================================
const OrcamentoFormModal = ({ orcamento, clientes: clientesProp, tamanhos, recheios, coberturas = [], fichasMassa, onClose, onSave }) => {
  const [clientesLocais, setClientesLocais] = useState(clientesProp);
  const clientes = clientesLocais;
  const [clienteId, setClienteId] = useState(orcamento?.cliente_id || '');
  const [clienteNome, setClienteNome] = useState(orcamento?.cliente_nome || '');
  const [clienteTelefone, setClienteTelefone] = useState(orcamento?.cliente_telefone || '');
  const [buscaCliente, setBuscaCliente] = useState('');
  const [showNovoCliente, setShowNovoCliente] = useState(false);
  // Ao editar, o backend devolve os ids em `recheios_ids`; orcamentos antigos
  // (de antes da coluna extra) vem so com recheio_principal_id.
  const [itens, setItens] = useState(orcamento?.itens?.map(i => ({
    ...i,
    recheios: (i.recheios_ids?.length ? i.recheios_ids : [i.recheio_principal_id].filter(Boolean)).map(String),
    preco_unitario: i.preco_unitario?.toFixed(2).replace('.', ',') || '',
  })) || [
    { tipo: 'bolo', tamanho_id: '', massa_ficha_id: '', recheios: [''], cobertura: '', decoracao: '', descricao_manual: '', quantidade: 1, preco_unitario: '' }
  ]);
  const [dataEntrega, setDataEntrega] = useState(orcamento?.data_entrega || '');
  const [validade, setValidade] = useState(orcamento?.validade || '');
  const [descontoTipo, setDescontoTipo] = useState('reais');
  const [desconto, setDesconto] = useState(orcamento?.desconto ? orcamento.desconto.toFixed(2).replace('.', ',') : '');
  const [observacoes, setObservacoes] = useState(orcamento?.observacoes || '');
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const clientesFiltrados = buscaCliente
    ? clientes.filter(c => norm(c.nome).includes(norm(buscaCliente)) || c.telefone.includes(buscaCliente))
    : clientes;

  const addItem = (tipo) => setItens([...itens, { tipo, tamanho_id: '', massa_ficha_id: '', recheios: [''], cobertura: '', decoracao: '', descricao_manual: '', quantidade: 1, preco_unitario: '' }]);
  const removeItem = (idx) => setItens(itens.filter((_, i) => i !== idx));
  const updateItem = (idx, field, value) => {
    const u = [...itens]; u[idx] = { ...u[idx], [field]: value };
    if (field === 'tamanho_id' && value) {
      const t = tamanhos.find(t => t.id === parseInt(value));
      if (t?.preco) u[idx].preco_unitario = t.preco.toFixed(2).replace('.', ',');
    }
    setItens(u);
  };

  const getRecheios = (tid) => { const t = tamanhos.find(t => t.id === parseInt(tid)); if (!t?.recheios_permitidos?.length) return recheios; const ids = t.recheios_permitidos.map(r => r.recheio_id); return recheios.filter(r => ids.includes(r.id)); };
  const getMassas = (tid) => { const t = tamanhos.find(t => t.id === parseInt(tid)); if (!t?.massas_permitidas?.length) return fichasMassa; const ids = t.massas_permitidas.map(m => m.ficha_tecnica_id); return fichasMassa.filter(f => ids.includes(f.id)); };

  const valorItens = itens.reduce((s, i) => s + parseMoeda(String(i.preco_unitario)) * (parseInt(i.quantidade) || 1), 0);
  const descontoValor = descontoTipo === 'percentual' ? valorItens * ((parseFloat(desconto) || 0) / 100) : parseMoeda(desconto);
  const valorTotal = Math.max(0, valorItens - descontoValor);

  const handleSubmit = async (e) => {
    e.preventDefault(); setErro(null); setSalvando(true);
    const payload = {
      cliente_id: clienteId || null,
      cliente_nome: clienteNome || null,
      cliente_telefone: clienteTelefone || null,
      data_entrega: dataEntrega || null,
      validade: validade || null,
      desconto: descontoValor,
      observacoes: observacoes || null,
      itens: itens.map(i => ({
        tipo: i.tipo,
        tamanho_id: i.tamanho_id ? parseInt(i.tamanho_id) : null,
        massa_ficha_id: i.massa_ficha_id ? parseInt(i.massa_ficha_id) : null,
        recheios: (i.recheios || []).filter(Boolean).map(Number),
        cobertura: i.cobertura || null, decoracao: i.decoracao || null,
        descricao_manual: i.descricao_manual || null,
        quantidade: parseInt(i.quantidade) || 1,
        preco_unitario: parseMoeda(String(i.preco_unitario)),
      })),
    };
    try {
      let res;
      if (orcamento?.id) res = await api.put(`/api/orcamentos/${orcamento.id}`, payload);
      else res = await api.post('/api/orcamentos', payload);
      onSave(res.data, !!orcamento?.id);
    } catch (err) { setErro(err.response?.data?.erro || 'Erro ao salvar.'); }
    finally { setSalvando(false); }
  };

  const criarClienteInline = async () => {
    if (!clienteNome.trim() || !clienteTelefone.trim()) return;
    try {
      const res = await api.post('/api/clientes', { nome: clienteNome, telefone: clienteTelefone });
      setClientesLocais([...clientes, res.data]);
      setClienteId(res.data.id);
      setShowNovoCliente(false); setBuscaCliente('');
    } catch {}
  };

  return (
    <div style={s.overlay}>
      <div style={{ ...s.modal, maxWidth: 580, maxHeight: '92vh' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{orcamento ? 'Editar Orçamento' : 'Criar Orçamento'}</h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#999' }}>Gere um orçamento profissional para o cliente</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#bbb' }}><FaTimes /></button>
        </div>

        {erro && <div style={s.error}>{erro}</div>}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Cliente */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <label style={s.label2}>Selecionar Cliente</label>
              <button type="button" onClick={() => setShowNovoCliente(!showNovoCliente)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#d4807c', fontWeight: 600 }}><FaPlus style={{ fontSize: 10 }} /> Novo Cliente</button>
            </div>
            {!showNovoCliente && !clienteId && (
              <>
                <input placeholder="Pesquisar clientes..." value={buscaCliente} onChange={e => setBuscaCliente(e.target.value)} style={s.inputM} />
                {buscaCliente && (
                  <div style={{ maxHeight: 120, overflow: 'auto', border: '1px solid #eee', borderRadius: 8, marginTop: 4 }}>
                    {clientesFiltrados.length > 0 ? clientesFiltrados.map(c => (
                      <div key={c.id} onClick={() => { setClienteId(c.id); setClienteNome(c.nome); setClienteTelefone(c.telefone); setBuscaCliente(''); }}
                        style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f5f5f5', fontSize: 14 }}>
                        <strong>{c.nome}</strong> <span style={{ color: '#999' }}>{c.telefone}</span>
                      </div>
                    )) : (
                      <div style={{ padding: 12, textAlign: 'center', color: '#999', fontSize: 13 }}>Não encontrado</div>
                    )}
                  </div>
                )}
              </>
            )}
            {clienteId && !showNovoCliente && (
              <div style={{ fontSize: 14, color: '#555' }}>
                Cliente: <strong>{clienteNome}</strong>
                <button type="button" onClick={() => { setClienteId(''); setClienteNome(''); setClienteTelefone(''); }} style={{ marginLeft: 8, color: '#999', background: 'none', border: 'none', cursor: 'pointer' }}><FaTimes /></button>
              </div>
            )}
            {showNovoCliente && (
              <div style={{ background: '#faf7f5', borderRadius: 12, padding: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginBottom: 8 }}>
                  <div><label style={{ fontSize: 12, color: '#888' }}>Nome</label><input value={clienteNome} onChange={e => setClienteNome(e.target.value)} placeholder="Maria Silva" style={s.inputM} /></div>
                  <div><label style={{ fontSize: 12, color: '#888' }}>WhatsApp</label><TelefoneInput value={clienteTelefone} onChange={setClienteTelefone} style={s.inputM} /></div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={criarClienteInline} style={s.btnSm}>Cadastrar e selecionar</button>
                  <button type="button" onClick={() => setShowNovoCliente(false)} style={{ ...s.btnSm, background: '#eee', color: '#666' }}>Cancelar</button>
                </div>
              </div>
            )}
          </div>

          {/* Nome e Telefone opcionais (se não selecionou cliente) */}
          {!clienteId && !showNovoCliente && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
              <div><label style={s.label2}>Nome do Cliente (opcional)</label><input value={clienteNome} onChange={e => setClienteNome(e.target.value)} placeholder="Ex: Maria Silva" style={s.inputM} /></div>
              <div><label style={s.label2}>WhatsApp (opcional)</label><TelefoneInput value={clienteTelefone} onChange={setClienteTelefone} style={s.inputM} /></div>
            </div>
          )}

          {/* Datas */}
          <div style={{ background: '#faf7f5', borderRadius: 12, padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 16 }}>📅</span>
              <label style={{ ...s.label2, margin: 0, fontWeight: 700 }}>Detalhes</label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
              <div><label style={{ fontSize: 12, color: '#888' }}>Data de Entrega</label><input type="date" value={dataEntrega} onChange={e => setDataEntrega(e.target.value)} style={s.inputM} /></div>
              <div><label style={{ fontSize: 12, color: '#888' }}>Validade (opcional)</label><input type="date" value={validade} onChange={e => setValidade(e.target.value)} style={s.inputM} /></div>
            </div>
          </div>

          {/* Itens */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label style={s.label2}>Itens</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" onClick={() => addItem('bolo')} style={s.btnSm}>Bolo</button>
                <button type="button" onClick={() => addItem('manual')} style={{ ...s.btnSm, background: '#eee', color: '#555' }}>+ Manual</button>
              </div>
            </div>
            {itens.map((item, idx) => (
              <div key={idx} style={{ background: '#faf7f5', borderRadius: 12, padding: 14, marginBottom: 10, position: 'relative' }}>
                <button type="button" onClick={() => removeItem(idx)} style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', color: '#ccc', cursor: 'pointer' }}><FaTimes /></button>
                <div style={{ fontSize: 12, color: '#999', marginBottom: 8, fontWeight: 600 }}>Item {idx + 1} — {item.tipo === 'bolo' ? 'Bolo' : 'Manual'}</div>
                {item.tipo === 'bolo' ? (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginBottom: 8 }}>
                      <div><label style={{ fontSize: 12, color: '#888' }}>Tamanho</label>
                        <select value={item.tamanho_id} onChange={e => updateItem(idx, 'tamanho_id', e.target.value)} style={s.inputM}>
                          <option value="">Selecione...</option>
                          {tamanhos.map(t => <option key={t.id} value={t.id}>{t.nome} ({formatFatiasKg(t.fatias, t.tipo_medida)})</option>)}
                        </select>
                      </div>
                      <div><label style={{ fontSize: 12, color: '#888' }}>Massa</label>
                        <select value={item.massa_ficha_id} onChange={e => updateItem(idx, 'massa_ficha_id', e.target.value)} style={s.inputM}>
                          <option value="">Selecione...</option>
                          {getMassas(item.tamanho_id).map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginBottom: 8 }}>
                      <div><label style={{ fontSize: 12, color: '#888' }}>Recheios</label>
                        {/* Mesmo controle do pedido: um bolo de tres camadas pode ter mais de
                            um recheio, e o orcamento vira pedido — se so cabe um aqui, o
                            segundo se perde no meio do caminho. */}
                        {(item.recheios || ['']).map((rId, ri) => (
                          <div key={ri} style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 4 }}>
                            <span style={{ fontSize: 11, color: '#aaa', width: 16 }}>{ri + 1}</span>
                            <select value={rId} onChange={e => {
                              const novos = [...(item.recheios || [''])];
                              novos[ri] = e.target.value;
                              updateItem(idx, 'recheios', novos);
                            }} style={{ ...s.inputM, flex: 1 }}>
                              <option value="">Selecione...</option>
                              {getRecheios(item.tamanho_id).map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
                            </select>
                            {ri > 0 && (
                              <button type="button" onClick={() => updateItem(idx, 'recheios', (item.recheios || []).filter((_, k) => k !== ri))}
                                style={{ background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', fontSize: 14 }}>✕</button>
                            )}
                          </div>
                        ))}
                        {(item.recheios || ['']).length < 3 && (
                          <button type="button" onClick={() => updateItem(idx, 'recheios', [...(item.recheios || ['']), ''])}
                            style={{ background: 'none', border: 'none', color: '#d4807c', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                            + Adicionar recheio
                          </button>
                        )}
                      </div>
                      <div><label style={{ fontSize: 12, color: '#888' }}>Cobertura</label>
                        <select value={item.cobertura || ''} onChange={e => updateItem(idx, 'cobertura', e.target.value)} style={s.inputM}>
                          <option value="">Selecione...</option>
                          {coberturas.map(c => <option key={c.id} value={c.nome}>{c.nome}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{ marginBottom: 8 }}><label style={{ fontSize: 12, color: '#888' }}>Decoração</label>
                      <input value={item.decoracao || ''} onChange={e => updateItem(idx, 'decoracao', e.target.value)} placeholder="Ex: Flores, tema..." style={s.inputM} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
                      <div><label style={{ fontSize: 12, color: '#888' }}>Qtd</label><input type="number" min="1" value={item.quantidade} onChange={e => updateItem(idx, 'quantidade', e.target.value)} style={s.inputM} /></div>
                      <div><label style={{ fontSize: 12, color: '#888' }}>Preço (R$)</label><input value={typeof item.preco_unitario === 'number' ? item.preco_unitario.toFixed(2).replace('.', ',') : item.preco_unitario} onChange={e => updateItem(idx, 'preco_unitario', formatarMoedaInput(e.target.value))} placeholder="0,00" style={s.inputM} /></div>
                    </div>
                  </>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                    <div><label style={{ fontSize: 12, color: '#888' }}>Descrição</label><input value={item.descricao_manual || ''} onChange={e => updateItem(idx, 'descricao_manual', e.target.value)} placeholder="Ex: Kit doces" style={s.inputM} /></div>
                    <div><label style={{ fontSize: 12, color: '#888' }}>Qtd</label><input type="number" min="1" value={item.quantidade} onChange={e => updateItem(idx, 'quantidade', e.target.value)} style={s.inputM} /></div>
                    <div><label style={{ fontSize: 12, color: '#888' }}>Preço (R$)</label><input value={typeof item.preco_unitario === 'number' ? item.preco_unitario.toFixed(2).replace('.', ',') : item.preco_unitario} onChange={e => updateItem(idx, 'preco_unitario', formatarMoedaInput(e.target.value))} placeholder="0,00" style={s.inputM} /></div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Desconto */}
          <div style={{ background: '#faf7f5', borderRadius: 12, padding: 14 }}>
            <label style={{ ...s.label2, marginBottom: 8 }}>Desconto (opcional)</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 12, fontSize: 14 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', color: descontoTipo === 'reais' ? '#d4807c' : '#999' }}>
                  <input type="radio" checked={descontoTipo === 'reais'} onChange={() => setDescontoTipo('reais')} style={{ accentColor: '#d4807c' }} /> R$ Valor Fixo
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', color: descontoTipo === 'percentual' ? '#d4807c' : '#999' }}>
                  <input type="radio" checked={descontoTipo === 'percentual'} onChange={() => setDescontoTipo('percentual')} style={{ accentColor: '#d4807c' }} /> % Porcentagem
                </label>
              </div>
              <input value={desconto} onChange={e => setDesconto(descontoTipo === 'reais' ? formatarMoedaInput(e.target.value) : e.target.value.replace(/[^0-9]/g, ''))}
                placeholder={descontoTipo === 'reais' ? 'Ex: 25,00' : 'Ex: 10'} style={{ ...s.inputM, width: 120 }} />
            </div>
          </div>

          {/* Observações */}
          <div><label style={s.label2}>Observações (opcional)</label>
            <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)} placeholder="Anotações que aparecerão no orçamento enviado ao cliente..." style={{ ...s.inputM, minHeight: 70, resize: 'vertical' }} />
          </div>

          {/* Resumo financeiro */}
          <div style={{ background: '#faf7f5', borderRadius: 12, padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 4 }}><span>Total Itens</span><span>{fmt(valorItens)}</span></div>
            {descontoValor > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#e74c3c', marginBottom: 4 }}><span>Desconto</span><span>-{fmt(descontoValor)}</span></div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 700, color: '#d4807c', borderTop: '1px solid #eee', paddingTop: 8, marginTop: 4 }}><span>Valor Final</span><span>{fmt(valorTotal)}</span></div>
          </div>

          {/* Botões */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding: '10px 24px', borderRadius: 10, border: '1px solid #ddd', background: '#fff', color: '#666', fontSize: 14, cursor: 'pointer' }}>Cancelar</button>
            <button type="submit" disabled={salvando} style={{ ...s.btnPrimary, background: '#d4807c', borderColor: '#d4807c' }}>
              {salvando ? 'Salvando...' : orcamento ? 'Salvar' : 'Criar Orçamento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


// ===========================================================================
// Detalhe Modal + PDF
// ===========================================================================
const OrcamentoDetalheModal = ({ orcamento, onClose }) => {
  const abrirWhatsApp = () => {
    if (!orcamento.cliente_telefone) return;
    const num = orcamento.cliente_telefone.replace(/\D/g, '');
    const itensTexto = orcamento.itens?.map(i => {
      const nome = i.tipo === 'bolo' ? `${i.tamanho_nome || 'Bolo'}${i.recheio_nome ? ` - ${i.recheio_nome}` : ''}` : i.descricao_manual;
      return `• ${nome} x${i.quantidade} — ${fmt((i.preco_unitario || 0) * i.quantidade)}`;
    }).join('\n');
    const msg = `Olá ${orcamento.cliente_nome}!\n\nSegue seu orçamento:\n\n${itensTexto}\n\n${orcamento.desconto > 0 ? `Desconto: -${fmt(orcamento.desconto)}\n` : ''}*Total: ${fmt(orcamento.valor_total)}*${orcamento.data_entrega ? `\nEntrega: ${fmtData(orcamento.data_entrega)}` : ''}${orcamento.validade ? `\nVálido até: ${fmtData(orcamento.validade)}` : ''}${orcamento.observacoes ? `\n\nObs: ${orcamento.observacoes}` : ''}`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const gerarPDF = (formato) => {
    const largura = formato === 'termica' ? '80mm' : '210mm';
    const fs = formato === 'termica' ? '11px' : '14px';

    const itensHtml = orcamento.itens?.map(i => {
      const nome = i.tipo === 'bolo' ? (i.tamanho_nome || 'Bolo') : (i.descricao_manual || 'Item');
      const detalhe = i.tipo === 'bolo' ? [i.massa_ficha_nome, i.recheio_nome, i.cobertura, i.decoracao].filter(Boolean).join(' · ') : '';
      return `<tr><td style="padding:6px 0;border-bottom:1px solid #f0f0f0">${nome} x${i.quantidade}${detalhe ? `<br><small style="color:#888">${detalhe}</small>` : ''}</td><td style="text-align:right;padding:6px 0;border-bottom:1px solid #f0f0f0;white-space:nowrap">${fmt((i.preco_unitario || 0) * i.quantidade)}</td></tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
      body { font-family: 'Segoe UI', sans-serif; margin: 0; padding: 24px; width: ${largura}; font-size: ${fs}; color: #333; }
      h1 { font-size: ${formato === 'termica' ? '16px' : '22px'}; margin: 0 0 4px; color: #2c3e50; }
      .subtitle { color: #999; font-size: ${formato === 'termica' ? '10px' : '13px'}; margin-bottom: 16px; }
      table { width: 100%; border-collapse: collapse; }
      .divider { border-top: 1px dashed #ddd; margin: 12px 0; }
      .total { font-weight: 700; font-size: ${formato === 'termica' ? '14px' : '18px'}; color: #d4807c; }
      .footer { margin-top: 24px; text-align: center; color: #bbb; font-size: 11px; }
    </style></head><body>
      <h1>Orçamento</h1>
      <div class="subtitle">${fmtData(orcamento.criado_em?.split('T')[0])}</div>
      ${orcamento.cliente_nome ? `<div style="margin-bottom:4px"><strong>${orcamento.cliente_nome}</strong></div>` : ''}
      ${orcamento.cliente_telefone ? `<div style="margin-bottom:8px;color:#888">📞 ${orcamento.cliente_telefone}</div>` : ''}
      ${orcamento.data_entrega ? `<div style="margin-bottom:4px">📅 Entrega: ${fmtData(orcamento.data_entrega)}</div>` : ''}
      ${orcamento.validade ? `<div style="margin-bottom:8px;color:#888">⏳ Válido até: ${fmtData(orcamento.validade)}</div>` : ''}
      <div class="divider"></div>
      <table>${itensHtml}</table>
      <div class="divider"></div>
      <div style="text-align:right"><div style="margin-bottom:4px">Total Itens: ${fmt(orcamento.valor_itens)}</div>
      ${orcamento.desconto > 0 ? `<div style="color:#e74c3c;margin-bottom:4px">Desconto: -${fmt(orcamento.desconto)}</div>` : ''}
      <div class="total">Valor Final: ${fmt(orcamento.valor_total)}</div></div>
      ${orcamento.observacoes ? `<div class="divider"></div><div style="color:#888"><strong>Observações:</strong><br>${orcamento.observacoes}</div>` : ''}
      <div class="footer">Orçamento sujeito a alterações</div>
    </body></html>`;

    // Mesmo conteudo do HTML acima, em blocos — e o que o app desenha no PDF.
    // Mexeu num, olhe o outro (o porque esta em platform/arquivos.js).
    const blocos = [
      { t: 'titulo', texto: `Orçamento #${orcamento.id}` },
      { t: 'linha', texto: orcamento.cliente_nome, forte: true },
      orcamento.cliente_telefone && { t: 'linha', texto: orcamento.cliente_telefone, fraco: true },
      orcamento.data_entrega && { t: 'linha', texto: `Entrega: ${fmtData(orcamento.data_entrega)}` },
      orcamento.validade && { t: 'linha', texto: `Válido até: ${fmtData(orcamento.validade)}`, fraco: true },
      { t: 'divisoria' },
      ...(orcamento.itens || []).map(i => ({
        t: 'item',
        nome: `${i.tipo === 'bolo' ? (i.tamanho_nome || 'Bolo') : (i.descricao_manual || 'Item')} x${Number(i.quantidade) || 1}`,
        detalhe: i.tipo === 'bolo'
          ? [i.massa_ficha_nome, i.recheio_nome, i.cobertura, i.decoracao].filter(Boolean).join(' | ')
          : '',
        valor: fmt((i.preco_unitario || 0) * (Number(i.quantidade) || 1)),
      })),
      { t: 'divisoria' },
      { t: 'par', esq: 'Total itens', dir: fmt((orcamento.itens || []).reduce(
        (soma, i) => soma + (i.preco_unitario || 0) * (Number(i.quantidade) || 1), 0)) },
      (orcamento.taxa_entrega || 0) > 0 && { t: 'par', esq: 'Taxa de entrega', dir: fmt(orcamento.taxa_entrega), fraco: true },
      (orcamento.desconto || 0) > 0 && { t: 'par', esq: 'Desconto', dir: `-${fmt(orcamento.desconto)}`, fraco: true },
      { t: 'par', esq: 'Valor final', dir: fmt(orcamento.valor_total), forte: true },
      orcamento.observacoes && { t: 'divisoria' },
      orcamento.observacoes && { t: 'linha', texto: `Observações: ${orcamento.observacoes}`, fraco: true },
      { t: 'espaco' },
      { t: 'linha', texto: 'Orçamento sujeito a alterações', fraco: true, alinhar: 'centro' },
    ];

    return imprimirRecibo({
      html,
      blocos,
      nome: `orcamento-${orcamento.id}.pdf`,
      larguraMm: formato === 'termica' ? 80 : 210,
    });
  };

  return (
    <div style={s.overlay}>
      <div style={{ ...s.modal, maxWidth: 480 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Orçamento #{orcamento.id}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#bbb' }}><FaTimes /></button>
        </div>

        {/* Ações */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginBottom: 16 }}>
          {/* No app os dois botoes levavam a mesma folha de compartilhamento —
              a diferenca (A4 x bobina de 80mm) so importa para quem tem a
              impressora do lado, e essa pessoa esta no computador. */}
          {isNative() ? (
            <button onClick={() => gerarPDF('a4')} title="Compartilhar"
              style={{ ...s.actionBtn, border: '1px solid #ddd' }}><FaShareAlt /> Compartilhar</button>
          ) : (
            <>
              <button onClick={() => gerarPDF('a4')} style={{ ...s.actionBtn, border: '1px solid #ddd' }}><FaFilePdf /> PDF</button>
              <button onClick={() => gerarPDF('termica')} style={{ ...s.actionBtn, border: '1px solid #ddd' }}><FaPrint /> Térmica</button>
            </>
          )}
          <button onClick={abrirWhatsApp} style={{ ...s.actionBtn, border: '1px solid #25d36640', color: '#25d366' }}><FaWhatsapp /> WhatsApp</button>
          <button onClick={() => { if (orcamento.cliente_telefone) { const num = orcamento.cliente_telefone.replace(/\D/g, ''); window.open(`mailto:?subject=Orçamento&body=${encodeURIComponent(`Orçamento: ${fmt(orcamento.valor_total)}`)}`); } }} style={{ ...s.actionBtn, border: '1px solid #3498db40', color: '#3498db' }}>✉️ Email</button>
        </div>

        {/* Info */}
        <div style={{ background: '#faf7f5', borderRadius: 12, padding: 14, marginBottom: 12 }}>
          {orcamento.cliente_nome && <div style={{ fontWeight: 600, marginBottom: 4 }}>{orcamento.cliente_nome}</div>}
          {orcamento.cliente_telefone && <div style={{ fontSize: 13, color: '#888' }}>📞 {orcamento.cliente_telefone}</div>}
        </div>

        {orcamento.data_entrega && <div style={{ fontSize: 14, marginBottom: 4 }}>📅 Entrega: {fmtData(orcamento.data_entrega)}</div>}
        {orcamento.validade && <div style={{ fontSize: 14, marginBottom: 8, color: '#888' }}>⏳ Válido até: {fmtData(orcamento.validade)}</div>}

        {/* Itens */}
        {orcamento.itens?.map(i => (
          <div key={i.id} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 500 }}>{i.tipo === 'bolo' ? i.tamanho_nome : i.descricao_manual} x{i.quantidade}</span>
              <span style={{ fontWeight: 600 }}>{fmt((i.preco_unitario || 0) * i.quantidade)}</span>
            </div>
            {i.tipo === 'bolo' && (
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                {[i.massa_ficha_nome, i.recheio_nome, i.cobertura, i.decoracao].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
        ))}

        <div style={{ borderTop: '1px solid #eee', paddingTop: 8, marginTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 2 }}><span>Total Itens</span><span>{fmt(orcamento.valor_itens)}</span></div>
          {orcamento.desconto > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#e74c3c' }}><span>Desconto</span><span>-{fmt(orcamento.desconto)}</span></div>}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 700, color: '#d4807c', marginTop: 4 }}><span>Valor Final</span><span>{fmt(orcamento.valor_total)}</span></div>
        </div>

        {orcamento.observacoes && (
          <div style={{ fontSize: 13, color: '#888', marginTop: 12 }}><strong>Observações:</strong> {orcamento.observacoes}</div>
        )}

        <div style={{ fontSize: 12, color: '#ccc', textAlign: 'center', marginTop: 16 }}>
          Criado em {orcamento.criado_em ? new Date(orcamento.criado_em).toLocaleString('pt-BR') : ''}
        </div>
      </div>
    </div>
  );
};


const s = {
  input: t.input, inputM: t.input,
  label2: { ...t.label, fontWeight: 700 },
  btnPrimary: t.btnPrimary, btnSm: t.btnSmall,
  iconBtn: t.btnIcon,
  card: { ...t.card, padding: '16px 20px' },
  overlay: t.overlay, modal: t.modal,
  error: t.error,
  actionBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 0', borderRadius: 10, background: '#fff', fontSize: 14, fontWeight: 500, cursor: 'pointer', color: theme.colors.textSecondary, border: `1px solid ${theme.colors.border}` },
};

export default Orcamentos;
